import ContentContainer from "Components/Container/ContentContainer";
import { BackButton, Button, Input } from "Components/CustomComponents";
import { LoginUser } from "Data/interfaces";
import { getSecureLocalStorage } from "Data/services/localStorageService";
import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { CameraIcon, EditProfileIcon } from "svg";
import styles from "./EditProfile.module.scss";
import useIsMounted from "Hooks/useIsMounted";
import { useUpdateProfileMutation } from "Data/services/userAuthApi";
import { ErrorToast, SuccessToast } from "Hooks/useToast";
import { getErrorMessage } from "Hooks/useErrorHandler";
import { updateUser } from "Data/features/authSlice";

export interface ProfileFormData {
  firstname: string;
  lastname: string;
  user_name: string;
  phone: string;
  password: string;
  truck_no: string;
}

const EditProfile: React.FC = () => {
  const { handleSubmit, control, setValue } = useForm<ProfileFormData>({
    defaultValues: {
      firstname: "",
      lastname: "",
    },
  });
  const isMounted = useIsMounted();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // const [phone, setPhone] = useState<string>();
  // const [userName, setUserName] = useState<string>();
  const [authUser, setAuthUser] = useState<LoginUser>();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  useEffect(() => {
    const storedUser = getSecureLocalStorage("authUser") as LoginUser | null;
    if (storedUser) {
      setAuthUser(storedUser as unknown as LoginUser);
      setValue("firstname", storedUser?.firstname || "");
      setValue("lastname", storedUser?.lastname || "");
      setValue("user_name", storedUser?.username || "");
      setValue("phone", storedUser?.mobile_no || "");
      // setPhone(storedUser?.mobile_no || "");
      // setUserName(storedUser?.username || "");
    }
  }, [isMounted.current]);

  const handleIconClick = () => {
    if (editable) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      // console.log("Selected File:", file);
      setSelectedFile(file);
      const imageUrl = URL.createObjectURL(file);
      setSelectedImage(imageUrl);
    }
  };

  const [editable, setEditable] = useState<boolean>(false);

  const handleEditable = () => {
    setEditable(!editable);
  };

  const handleProfile = async (data: ProfileFormData) => {
    try {
      const formData = new FormData();

      if (selectedFile) {
        formData.append("image_url", selectedFile);
      }
      formData.append("firstname", data.firstname || "");
      formData.append("lastname", data.lastname || "");

      // Log FormData content
      // for (const pair of formData.entries()) {
      //   console.log(pair[0], pair[1]);
      // }
      const response = selectedFile
        ? await updateProfile(formData).unwrap()
        : await updateProfile(data).unwrap();

      if (response.code !== 200) {
        ErrorToast(response.message);
      }
      if (response.code === 200) {
        updateUser(response.data);
        SuccessToast(response.message);
      }
    } catch (error) {
      // console.log("Profile Update Error:", error);
      ErrorToast(getErrorMessage(error));
    }
  };

  return (
    <div className={styles.EditProfile}>
      <BackButton isRelative={true} text="Edit Profile" classes="mb-1" />

      <ContentContainer
        bg="#ffffff"
        classes="d-flex justify-content-center p-4"
        style={{ borderRadius: "12px" }}
        hasBoxShadow={true}
      >
        <div className={`${styles.profileCard}`}>
          <Form onSubmit={handleSubmit(handleProfile)}>
            <div
              className={`${styles.editProfileBtn}`}
              onClick={handleEditable}
            >
              <EditProfileIcon />
              <span className="px-2">Edit Profile</span>
            </div>

            <div className="d-flex justify-content-center w-100 mb-5">
              <div className="position-relative mt-5 mt-sm-0">
                <img
                  alt=""
                  src={selectedImage || authUser?.image_url}
                  width={"150px"}
                  height={"150px"}
                  className={`${styles.profileImage} rounded-circle`}
                />

                <div
                  className={`${styles.cameraBtn} rounded-circle `}
                  style={{ cursor: editable ? "pointer" : "not-allowed" }}
                  onClick={handleIconClick}
                >
                  <CameraIcon />
                </div>
                <input
                  type={editable ? "file" : "button"}
                  name="image_url"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </div>
            </div>

            <Row className="gap-3 gap-md-0">
              <Col sm={12} md={6}>
                <Controller
                  control={control}
                  name="firstname"
                  render={({ field: { onChange, value } }) => (
                    <Input
                      classes={`${editable ? "" : "border-0"}`}
                      placeholder="First Name"
                      type="text"
                      value={value}
                      onChange={onChange}
                      disable={!editable}
                    />
                  )}
                />
              </Col>
              <Col sm={12} md={6}>
                <Controller
                  control={control}
                  name="lastname"
                  render={({ field: { onChange, value } }) => (
                    <Input
                      classes={`${editable ? "" : "border-0"} `}
                      placeholder="last Name"
                      type="text"
                      value={value}
                      onChange={onChange}
                      disable={!editable}
                    />
                  )}
                />
              </Col>
            </Row>

            <Controller
              control={control}
              name="phone"
              render={({ field: { onChange, value } }) => (
                <PhoneInput
                  inputClass={`${editable ? "" : "border-0"}`}
                  placeholder="Phone Number"
                  country={"us"}
                  value={value}
                  onChange={(phone) => onChange(phone)}
                  disabled={true}
                />
              )}
            />

            <Controller
              control={control}
              name="user_name"
              render={({ field: { onChange, value } }) => (
                <Input
                  classes={`${editable ? "" : "border-0"} my-3`}
                  style={{ cursor: "not-allowed" }}
                  placeholder="User Name"
                  type="text"
                  value={value}
                  onChange={onChange}
                  disable={true}
                />
              )}
            />

            <Row>
              <Col sm={12} md={12}>
                <Button
                  text={
                    isLoading ? (
                      <Spinner animation="border" variant="light" size="sm" />
                    ) : (
                      "Save"
                    )
                  }
                  classes={`${editable ? "blackBg" : ""}`}
                  type={editable ? "submit" : "button"}
                  disabled={false}
                />
              </Col>
            </Row>
          </Form>
        </div>
      </ContentContainer>
    </div>
  );
};

export default EditProfile;
